'use client'

import TypographyWrapper from '@/components/comman/TypographyWrapper/TypographyWrapper'
import { useIntersectionAnimation } from '@/hooks/useIntersectionAnimation'
import { cn } from '@pw-tech/omni-ui'
import { useEffect, useState } from 'react'
import { CardItem, StatsProps } from './schema'
import StatsItem from './StatsItem'

export default function Stats({ cardItems, title, outlined, negTopMargin }: StatsProps) {
  const { ref, isVisible } = useIntersectionAnimation({
    threshold: 0.1,
    rootMargin: '0px 0px -10% 0px'
  })
  
  const [isLargeScreen, setIsLargeScreen] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 640)
    }
    
    handleResize() // Set initial value
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <div
      ref={ref}
      className={cn({
        'py-10': !outlined,
        '!z-30 w-full absolute': outlined,
        'lg:-mt-8 -mt-8': negTopMargin,
      })}
    >
      <div className='max-w-6xl mx-auto px-4 md:px-6'>
        <div
          className={cn('flex flex-col', {
            'shadow-sm rounded-md bg-white py-4 px-2.5 sm:px-5 border': outlined,
            ' gap-6 sm:gap-8': !outlined,
          })}
          style={outlined ? { borderColor: 'rgb(249 249 249/var(--tw-border-opacity,1))' } : {}}
        >
          <div
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 0.8s ease-in-out, transform 0.8s ease-in-out'
            }}
          >
            <TypographyWrapper
              variant='heading1'
              weight='semi-bold'
              color='#1b2124'
              component='div'
              className='text-center w-full break-words sm:leading-12 lg:leading-[50px]'
            >
              <span className='text-[#1b2124]'>{title}</span>
            </TypographyWrapper>
          </div>

          {cardItems && cardItems?.length > 0 && (
            <div
              className="grid justify-items-center content-center"
              style={{
                gridTemplateColumns: isLargeScreen ? 'repeat(4, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))',
                gap: outlined ? (isLargeScreen ? '0px' : '14px') : '12px',
                padding: outlined ? '0' : '10px'
              }}
            >
              {cardItems?.map((data: CardItem, index: number) => {
                const isLastItem = index === cardItems.length - 1
                const delay = index * 150
                
                return (
                  <div 
                    key={index} 
                    className='w-full'
                    style={{
                      opacity: isVisible ? 1 : 0,
                      transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                      transition: `opacity 0.6s ease-in-out ${delay}ms, transform 0.6s ease-in-out ${delay}ms`
                    }}
                  >
                    <StatsItem
                      itemData={data}
                      index={index}
                      isLastItem={isLastItem}
                      outlined={outlined}
                    />
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
